import React, { useState, useEffect } from 'react';
import { AppProviders } from '@/components/providers/AppProviders';
import { AppRoutes } from '@/routes/AppRoutes';
import { preloadIdleRoutes } from '@/lib/routePreload';
import { startVersionCheck } from '@/utils/versionCheck';
import { installFetchInterceptor } from '@/lib/fetchInterceptor';
import { NotificationService } from '@/services/NotificationService';
import { useReferralCapture } from '@/hooks/useReferralCapture';

// Debug logging
console.log('[App] App.tsx loaded successfully');

// Instalar interceptor de fetch para tratar erros de sessão
installFetchInterceptor();

/**
 * Componente de erro para falhas críticas da aplicação
 */
const ErrorScreen: React.FC<{ error: string; onReload: () => void }> = ({ error, onReload }) => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f0f0f0',
    fontFamily: 'system-ui, sans-serif'
  }}>
    <div style={{ textAlign: 'center', maxWidth: '600px', padding: '2rem' }}>
      <h1 style={{ fontSize: '2rem', marginBottom: '1rem', color: '#333' }}>
        ⚠️ Erro na aplicação
      </h1>
      <p style={{ color: '#666', marginBottom: '2rem' }}>
        {error}
      </p>
      <button
        onClick={onReload}
        style={{
          padding: '0.5rem 1rem',
          backgroundColor: '#2196f3',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer'
        }}
      >
        Recarregar Página
      </button>
    </div>
  </div>
);

const App = () => {
  console.log('[App] Rendering App component');
  
  const [error, setError] = useState<string | null>(null);

  // Capture referral code from URL if present
  useReferralCapture();

  // Preload critical routes during idle time
  useEffect(() => {
    // Wait for initial render to complete
    const timer = setTimeout(() => {
      preloadIdleRoutes();
      console.log('[App] Started preloading critical routes');
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Initialize notifications
  useEffect(() => {
    NotificationService.init();
  }, []);

  // Start version checking to detect new deployments
  useEffect(() => {
    startVersionCheck();
  }, []);

  // Estado de erro crítico
  if (error) {
    return <ErrorScreen error={error} onReload={() => window.location.reload()} />;
  }

  // Renderização principal com try-catch para erros críticos
  try {
    console.log('[App] About to render components');
    
    return (
      <AppProviders>
        <AppRoutes />
      </AppProviders>
    );
  } catch (error) {
    console.error('[App] Critical error rendering App:', error);
    
    const errorMessage = error instanceof Error ? error.message : String(error);
    setError(`Erro na aplicação: ${errorMessage}`);
    
    // Fallback imediato em caso de erro
    return <ErrorScreen error={errorMessage} onReload={() => window.location.reload()} />;
  }
};

export default App;
